import React, { useState, useEffect } from 'react'
import AsyncCreatableSelect from 'react-select/async-creatable'
import { showSuccess, showError } from '../utils/notifications.js'
import { t } from '../../i18n.js'

// Fund selection with async search and creation (depends on client).
// `translations` is the new_valuation.fund_select subtree from Rails.
export default function FundSelect({ clientId, value, onChange, searchUrl, createUrl, disabled, csrfToken, translations }) {
  const [selectedOption, setSelectedOption] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [key, setKey] = useState(0) // For forcing refresh

  // Reset selected option when client changes
  useEffect(() => {
    setSelectedOption(null)
    setKey(prev => prev + 1)
  }, [clientId])

  const loadOptions = async (inputValue) => {
    if (!clientId) return []

    const response = await fetch(`${searchUrl}?client_id=${clientId}&q=${encodeURIComponent(inputValue)}`)
    const data = await response.json()
    return data.map(item => ({ value: item.id, label: item.title }))
  }

  const handleCreate = async (inputValue) => {
    setIsLoading(true)
    try {
      const response = await fetch(createUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({ fund: { title: inputValue, client_id: clientId } })
      })

      const data = await response.json()
      if (data.success) {
        const newOption = { value: data.id, label: data.title }
        setSelectedOption(newOption)
        onChange(data.id, data.title)
        setKey(prev => prev + 1) // Force refresh to reload options
        showSuccess(t('created', translations, { title: data.title }))
        return newOption
      } else {
        showError(data.errors ? Object.values(data.errors).flat().join(', ') : t('failure', translations))
      }
    } catch (error) {
      showError(t('failure', translations))
      console.error('Error creating fund:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="mb-3">
      <label className="form-label">{t('label', translations)}</label>
      <AsyncCreatableSelect
        key={key}
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        value={selectedOption}
        onChange={(option) => {
          setSelectedOption(option)
          onChange(option?.value || null, option?.label || null)
        }}
        onCreateOption={handleCreate}
        placeholder={disabled ? t('placeholder_disabled', translations) : t('placeholder', translations)}
        isClearable
        isDisabled={disabled || isLoading}
        isLoading={isLoading}
        formatCreateLabel={(inputValue) => t('create_label', translations, { input: inputValue })}
        className="react-select-container"
        classNamePrefix="react-select"
      />
      {disabled && (
        <div className="form-text">{t('help_disabled', translations)}</div>
      )}
      {!disabled && (
        <div className="form-text">{t('help', translations)}</div>
      )}
    </div>
  )
}
